// src/components/calendar/navigation/CalendarHeader/CalendarHeaderShadcn.jsx
import React from 'react';
import { Plus } from 'lucide-react';

// shadcn components
import { Button } from '../../../ui/button';

import { cn } from '../../../../utils/cn';
import { CalendarNavigationButtonsShadcn } from '../../../ui/calendar/navigation/CalendarNavigationButtons';
import { CalendarViewToggleShadcn } from '../../../ui/calendar/navigation/CalendarViewToggle';
import CalendarTimeRangeToggleShadcn from '../../../ui/calendar/navigation/CalendarTimeRangeToggle/CalendarTimeRangeToggleShadcn';

const CalendarHeaderShadcn = ({
  // Navigation props
  currentView,
  currentTitle,
  onViewChange,
  onPrevious,
  onNext,
  onToday,

  // Create event props
  showCreateButton = true,
  onCreateEvent,
  isAdmin = false,

  // Time range props
  currentTimeRange,
  onTimeRangeChange,

  // State props
  loading = false,

  // Layout props
  compact = false,
  className = ''
}) => {
  if (compact) {
    // Compact layout for smaller screens
    return (
      <div className={cn(
        "flex flex-col gap-3 p-4 bg-slate-800/30 backdrop-blur-xl rounded-xl border border-slate-700/50",
        className
      )}>
        {/* First row: Navigation + Create */}
        <div className="flex items-center justify-between">
          <CalendarNavigationButtonsShadcn
            onPrevious={onPrevious}
            onNext={onNext}
            onToday={onToday}
            loading={loading}
          />

          {showCreateButton && isAdmin && (
            <Button
              onClick={onCreateEvent}
              disabled={loading}
              className={cn(
                "p-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600",
                "text-white hover:from-blue-500 hover:to-purple-500",
                "transition-all duration-200 hover:scale-105 active:scale-95 h-auto",
                "shadow-lg shadow-blue-500/25"
              )}
              title="Create Event"
            >
              <Plus className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Second row: Title */}
        {currentTitle && (
          <div className="text-center">
            <h2 className="text-lg font-bold text-slate-200">
              {currentTitle}
            </h2>
          </div>
        )}

        {/* Third row: Time range + View toggle */}
        <div className="flex justify-center gap-2">
          {/* Only show time range for Day and Week views */}
          {(currentView === 'timeGridWeek' || currentView === 'timeGridDay') && currentTimeRange && (
            <CalendarTimeRangeToggleShadcn
              currentRange={currentTimeRange}
              onRangeChange={onTimeRangeChange}
              compact={true}
            />
          )}
          <CalendarViewToggleShadcn
            currentView={currentView}
            onViewChange={onViewChange}
            compact={true}
          />
        </div>
      </div>
    );
  }

  // Full layout for desktop
  return (
    <div className={cn(
      "flex items-center justify-between p-4 bg-slate-800/30 backdrop-blur-xl rounded-xl border border-slate-700/50",
      className
    )}>
      {/* Left side: Navigation */}
      <CalendarNavigationButtonsShadcn
        onPrevious={onPrevious}
        onNext={onNext}
        onToday={onToday}
        currentTitle={currentTitle}
        loading={loading}
      />

      {/* Right side: Time range + View toggle + Create button */}
      <div className="flex items-center gap-4">
        {/* Only show time range for Day and Week views */}
        {(currentView === 'timeGridWeek' || currentView === 'timeGridDay') && currentTimeRange && (
          <CalendarTimeRangeToggleShadcn
            currentRange={currentTimeRange}
            onRangeChange={onTimeRangeChange}
            compact={false}
          />
        )}

        <CalendarViewToggleShadcn
          currentView={currentView}
          onViewChange={onViewChange}
          compact={false}
        />

        {showCreateButton && isAdmin && (
          <Button
            onClick={onCreateEvent}
            disabled={loading}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-xl h-auto",
              "bg-gradient-to-r from-blue-600 to-purple-600",
              "text-white hover:from-blue-500 hover:to-purple-500",
              "transition-all duration-200 hover:scale-105 active:scale-95",
              "shadow-lg shadow-blue-500/25 font-medium"
            )}
          >
            <Plus className="w-4 h-4" />
            Create Event
          </Button>
        )}
      </div>
    </div>
  );
};

export default CalendarHeaderShadcn;